import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  ExternalLink,
  Link as LinkIcon,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TagBadge } from "@/components/ui/TagBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { FileTypeIcon } from "./FileTypeIcon";
import { LinkExistingMediaModal } from "./LinkExistingMediaModal";
import {
  formatFileSize,
  useAddExternalLink,
  useDeleteMediaFile,
  useDownloadFile,
  useMediaCollections,
  useMediaList,
  useMediaTags,
  useUploadFile,
  type LinkExistingTargetType,
} from "../hooks/useMedia";
import type { MediaFile } from "../types";

type Props = {
  entityType?: string;
  entityId?: string;
  entityLabel?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
  taskId?: string;
  actionId?: string;
  noteId?: string;
  milestoneId?: string;
  budgetId?: string;
  expenseId?: string;
  onUploaded?: (file: MediaFile) => void;
  showExisting?: boolean;
  compact?: boolean;
  /**
   * Slim variant (used for QA issue attachments): hides the vault-related
   * actions — "Add External Link", "Link from Media Vault" and the
   * "View all in Media Vault" link — leaving only upload / download / delete.
   */
  hideVaultActions?: boolean;
};

type QueueItem = {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "done" | "error";
  error?: string;
};

export function FileUploadZone(props: Props) {
  const {
    entityType,
    entityId,
    entityLabel,
    companyId,
    productId,
    projectId,
    meetingId,
    taskId,
    actionId,
    noteId,
    milestoneId,
    budgetId,
    expenseId,
    onUploaded,
    showExisting = true,
    compact = false,
    hideVaultActions = false,
  } = props;

  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkVaultOpen, setLinkVaultOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({
    title: "",
    url: "",
    description: "",
  });
  const [linkTags, setLinkTags] = useState<string[]>([]);
  const [linkCollectionId, setLinkCollectionId] = useState<string>("");
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [fileToDelete, setFileToDelete] = useState<MediaFile | null>(null);

  // Derive the "primary" target the Link-Existing modal should set on the
  // selected MediaFile records. The most specific module-record id wins;
  // company/product/project are used only as a context filter, never as a
  // primary link target (they're always paired with a more specific id
  // in practice). When the host page doesn't pass a record-level id, the
  // modal is hidden — there's nothing meaningful to link to.
  const linkTarget = ((): {
    type: LinkExistingTargetType;
    id: string;
    label?: string | null;
  } | null => {
    const ordered: Array<[LinkExistingTargetType, string | undefined]> = [
      ["expense", expenseId],
      ["meeting", meetingId],
      ["task", taskId],
      ["action", actionId],
      ["note", noteId],
      ["milestone", milestoneId],
      ["budget", budgetId],
    ];
    for (const [type, id] of ordered) {
      if (id) return { type, id, label: entityLabel ?? null };
    }
    if (entityType && entityId) {
      // Polymorphic linkage for modules without a direct FK (idea, decision,
      // asset, lead, enquiry…). The backend accepts the entity type string
      // verbatim.
      const lowered = entityType.toLowerCase() as LinkExistingTargetType;
      return { type: lowered, id: entityId, label: entityLabel ?? null };
    }
    return null;
  })();

  const tagOptions = useMediaTags().data ?? [];
  const { data: collections = [] } = useMediaCollections();

  const existingFilter = {
    entityType,
    entityId,
    companyId,
    productId,
    projectId,
    meetingId,
    taskId,
    actionId,
    noteId,
    milestoneId,
    budgetId,
    expenseId,
  };

  const { data: existing } = useMediaList(
    showExisting ? { ...existingFilter, limit: 20 } : {},
  );

  const uploader = useUploadFile();
  const addLink = useAddExternalLink();
  const deleteMutation = useDeleteMediaFile();
  const downloadMutation = useDownloadFile();

  async function uploadFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    const items: QueueItem[] = arr.map((f) => ({
      id: `${Date.now()}-${f.name}-${Math.random().toString(36).slice(2)}`,
      file: f,
      progress: 0,
      status: "pending",
    }));
    setQueue((q) => [...q, ...items]);

    for (const item of items) {
      setQueue((q) =>
        q.map((qi) =>
          qi.id === item.id ? { ...qi, status: "uploading" } : qi,
        ),
      );
      try {
        const uploaded = await uploader.mutate({
          file: item.file,
          tags: pendingTags.length > 0 ? pendingTags : undefined,
          entityType,
          entityId,
          entityLabel,
          companyId,
          productId,
          projectId,
          meetingId,
          taskId,
          actionId,
          noteId,
          milestoneId,
          budgetId,
          expenseId,
        });
        setQueue((q) =>
          q.map((qi) =>
            qi.id === item.id
              ? { ...qi, status: "done", progress: 100 }
              : qi,
          ),
        );
        onUploaded?.(uploaded);
      } catch (err) {
        setQueue((q) =>
          q.map((qi) =>
            qi.id === item.id
              ? {
                  ...qi,
                  status: "error",
                  error: err instanceof Error ? err.message : "Upload failed",
                }
              : qi,
          ),
        );
      }
    }

    setTimeout(() => {
      setQueue((q) => q.filter((qi) => qi.status !== "done"));
    }, 2000);
  }

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      uploadFiles(e.target.files);
      e.target.value = "";
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  }

  async function submitLink(e: React.FormEvent) {
    e.preventDefault();
    if (!linkForm.title.trim() || !linkForm.url.trim()) return;
    await addLink.mutateAsync({
      title: linkForm.title.trim(),
      externalUrl: linkForm.url.trim(),
      description: linkForm.description.trim() || undefined,
      tags: linkTags.length > 0 ? linkTags : undefined,
      collectionId: linkCollectionId || undefined,
      entityType,
      entityId,
      entityLabel,
      companyId,
      productId,
      projectId,
      meetingId,
      taskId,
      actionId,
      noteId,
      milestoneId,
      budgetId,
      expenseId,
    });
    setLinkForm({ title: "", url: "", description: "" });
    setLinkTags([]);
    setLinkCollectionId("");
    setLinkModalOpen(false);
  }

  function toggleLinkTag(value: string) {
    setLinkTags((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }

  function togglePendingTag(value: string) {
    setPendingTags((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }

  function navigateToVault() {
    const params = new URLSearchParams();
    if (entityType) params.set("entityType", entityType);
    if (entityId) params.set("entityId", entityId);
    navigate(`/media-vault${params.toString() ? `?${params}` : ""}`);
  }

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={onFileInputChange}
      />

      <div
        className={cn(
          "rounded-xl border-2 border-dashed transition-colors text-center",
          compact ? "p-4" : "p-6",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-muted-foreground/40",
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
      >
        <Upload
          size={compact ? 24 : 32}
          className="mx-auto text-muted-foreground"
        />
        <div className={cn("mt-2 font-medium", compact ? "text-xs" : "text-sm")}>
          Drop files here or click to upload
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          Max file size: 50MB
        </div>
      </div>

      {!hideVaultActions && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLinkModalOpen(true)}
          >
            <LinkIcon size={12} /> Add External Link
          </Button>
          {linkTarget && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLinkVaultOpen(true)}
            >
              <LinkIcon size={12} /> Link from Media Vault
            </Button>
          )}
        </div>
      )}

      {tagOptions.length > 0 && (
        <div>
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Tags
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {tagOptions.map((opt) => {
              const selected = pendingTags.includes(opt.value);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => togglePendingTag(opt.value)}
                  style={{ opacity: selected ? 1 : 0.5 }}
                >
                  <TagBadge value={opt.value} label={opt.label} size="sm" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {queue.length > 0 && (
        <div className="space-y-1.5">
          {queue.map((q) => (
            <div
              key={q.id}
              className="flex items-center gap-2 rounded border px-2 py-1.5 text-xs"
            >
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{q.file.name}</div>
                <div className="mt-1 h-1 rounded bg-muted overflow-hidden">
                  <div
                    className={cn(
                      "h-full transition-all",
                      q.status === "error" ? "bg-destructive" : "bg-primary",
                    )}
                    style={{
                      width:
                        q.status === "done"
                          ? "100%"
                          : q.status === "uploading"
                            ? "65%"
                            : q.status === "error"
                              ? "100%"
                              : "0%",
                    }}
                  />
                </div>
                {q.error && (
                  <div className="mt-0.5 text-[10px] text-destructive">
                    {q.error}
                  </div>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground">
                {formatFileSize(q.file.size)}
              </span>
              <span className="text-[10px]">
                {q.status === "done"
                  ? "✓"
                  : q.status === "error"
                    ? "✗"
                    : `${q.progress}%`}
              </span>
            </div>
          ))}
        </div>
      )}

      {showExisting && existing && existing.data.length > 0 && (
        <div className="space-y-1.5">
          {existing.data.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-2 rounded border px-2 py-1.5 text-xs bg-white"
            >
              <FileTypeIcon fileType={f.fileType} size={24} />
              <button
                type="button"
                onClick={() => navigate(`/media-vault/${f.id}`)}
                className="flex-1 min-w-0 text-left"
              >
                <div
                  className="truncate font-medium hover:text-primary"
                  title={f.originalName ?? f.title}
                >
                  {f.title || f.originalName}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {formatFileSize(f.fileSize)} · {new Date(f.createdAt).toLocaleDateString()}
                </div>
              </button>
              <button
                type="button"
                onClick={() =>
                  downloadMutation.mutate({
                    id: f.id,
                    originalName: f.originalName,
                    isExternal: f.isExternal,
                    externalUrl: f.externalUrl,
                  })
                }
                className="p-1 rounded hover:bg-muted"
                aria-label="Download"
              >
                {f.isExternal ? <ExternalLink size={12} /> : <Download size={12} />}
              </button>
              <button
                type="button"
                onClick={() => setFileToDelete(f)}
                className="p-1 rounded hover:bg-muted text-destructive"
                aria-label="Delete"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showExisting && !hideVaultActions && (
        <button
          type="button"
          onClick={navigateToVault}
          className="text-xs text-primary hover:underline"
        >
          View all in Media Vault →
        </button>
      )}

      <ConfirmDialog
        open={fileToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
        title="Delete file?"
        description={
          fileToDelete
            ? `Are you sure you want to delete '${fileToDelete.title}'? It will be removed from your uploads.`
            : ""
        }
        onConfirm={async () => {
          if (!fileToDelete) return;
          await deleteMutation.mutateAsync(fileToDelete.id);
        }}
      />

      {linkTarget && (
        <LinkExistingMediaModal
          open={linkVaultOpen}
          onClose={() => setLinkVaultOpen(false)}
          target={linkTarget}
          excludeIds={(existing?.data ?? []).map((f) => f.id)}
          context={{ companyId, productId, projectId }}
        />
      )}

      <Dialog open={linkModalOpen} onOpenChange={setLinkModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add External Link</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitLink} className="space-y-3">
            <div>
              <label className="text-xs font-medium">Title</label>
              <input
                type="text"
                value={linkForm.title}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, title: e.target.value }))
                }
                required
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium">URL</label>
              <input
                type="url"
                value={linkForm.url}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, url: e.target.value }))
                }
                required
                placeholder="https://…"
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Description</label>
              <textarea
                value={linkForm.description}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Collection</label>
              <select
                value={linkCollectionId}
                onChange={(e) => setLinkCollectionId(e.target.value)}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
              >
                <option value="">No collection</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Tags</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {tagOptions.length === 0 ? (
                  <span className="text-[11px] italic text-muted-foreground">
                    No tag options configured
                  </span>
                ) : (
                  tagOptions.map((opt) => {
                    const selected = linkTags.includes(opt.value);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleLinkTag(opt.value)}
                        style={{ opacity: selected ? 1 : 0.5 }}
                      >
                        <TagBadge
                          value={opt.value}
                          label={opt.label}
                          size="sm"
                        />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLinkModalOpen(false)}
              >
                <X size={12} /> Cancel
              </Button>
              <Button type="submit" size="sm" disabled={addLink.isPending}>
                Add Link
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
